import { db, auth } from "./firebase";
import { ref, set, get, update, onValue, off, runTransaction } from "firebase/database";

// ── Generate a 4 digit invite code ───────────────────────────
export const generateCode = () => {
  return Math.floor(1000 + Math.random() * 9000).toString();
};

// ── Create a new match ────────────────────────────────────────
// MP-FIX: codes are only 4 digits (9000 combos) and matches are never
// deleted, so a fresh code could collide with a LIVE match — and set()
// would obliterate it mid-game. We now probe up to 20 codes and only
// accept one that is free, finished, or stale (>24h old).
const CODE_STALE_MS = 24 * 60 * 60 * 1000;
const findFreeCode = async () => {
  for (let i = 0; i < 20; i++) {
    const candidate = generateCode();
    const snap = await get(ref(db, `matches/${candidate}`));
    if (!snap.exists()) return candidate;
    const m = snap.val();
    const isDead = m.status === "finished" ||
                   (Date.now() - (m.createdAt || 0)) > CODE_STALE_MS;
    if (isDead) return candidate; // safe to recycle
  }
  return null; // astronomically unlikely — DB nearly full of live matches
};

export const createMatch = async (playerName, grimoire, luxmoire, prophecy, hand, curios = [null, null]) => {
  // VACUUM-SEAL: a real account is required to host. We stamp the host's
  // uid onto seat 0 so the DB rules can verify only the two seated players
  // ever write to this match.
  const myUid = auth.currentUser && auth.currentUser.uid;
  if (!myUid) throw new Error("You must be signed in to host a match.");

  const code = await findFreeCode();
  if (!code) throw new Error("No free match codes — try again in a moment.");
  const matchRef = ref(db, `matches/${code}`);

  const initialState = {
    code,
    status: "waiting",
    createdAt: Date.now(),
    winner: null,
    turn: 0,
    activePlayer: 0,
    solrendReady: { 0: false, 1: false },
    players: {
      0: {
        uid: myUid,
        name: playerName,
        anima: 30,
        maxAnima: 50,
        chao: 3,
        maxChao: 3,
        grimoire: grimoire || {},
        luxmoire: luxmoire || {},
        prophecy: prophecy || null,
        hand: hand || [],
        field: {
          esclings: [
            { id: "slot0", escling: null, escendant: null, soul: [] },
            { id: "slot1", escling: null, escendant: null, soul: [] },
            { id: "slot2", escling: null, escendant: null, soul: [] },
          ],
          tomes: [null, null, null],
          curios: curios || [null, null],
          prophecy: prophecy || null,
        },
        necropolis: [],
        shadowGamblingStreak: 0,
        totalAnimaLost: 0,
        animaLostThisTurn: 0,
        animaGainedThisTurn: 0,
        animaGainedLastTurn: 0,
        ready: true,
      },
      1: null,
    },
  };

  await set(matchRef, initialState);
  return code;
};

// ── Join an existing match ─────────────────────────────────────
// MP-FIX: slot claim is now a TRANSACTION. The old read-check-write had a
// race — two joiners could both pass the "players[1] empty" check and the
// second write silently hijacked the first player's seat.
export const joinMatch = async (code, playerName, grimoire, luxmoire, prophecy, hand, curios = [null, null]) => {
  // VACUUM-SEAL: a real account is required to join. We stamp the joiner's
  // uid onto seat 1 so the DB rules can verify only the two seated players
  // ever write to this match.
  const myUid = auth.currentUser && auth.currentUser.uid;
  if (!myUid) return { error: "You must be signed in to join a match." };

  const matchRef = ref(db, `matches/${code}`);
  const snapshot = await get(matchRef);

  if (!snapshot.exists()) return { error: "Match not found." };

  const match = snapshot.val();
  if (match.status !== "waiting") return { error: "Match already in progress." };
  if (match.players[1]) return { error: "Match is full." };

  const player2 = {
    uid: myUid,
    name: playerName,
    anima: 30,
    maxAnima: 50,
    chao: 3,
    maxChao: 3,
    grimoire: grimoire || {},
    luxmoire: luxmoire || {},
    prophecy: prophecy || null,
    hand: hand || [],
    field: {
      esclings: [
        { id: "slot0", escling: null, escendant: null, soul: [] },
        { id: "slot1", escling: null, escendant: null, soul: [] },
        { id: "slot2", escling: null, escendant: null, soul: [] },
      ],
      tomes: [null, null, null],
      curios: curios || [null, null],
      prophecy: prophecy || null,
    },
    necropolis: [],
    shadowGamblingStreak: 0,
    totalAnimaLost: 0,
    animaLostThisTurn: 0,
    animaGainedThisTurn: 0,
    animaGainedLastTurn: 0,
    ready: true,
  };

  // Atomically claim slot 1 — aborts if someone beat us to it
  const result = await runTransaction(matchRef, (current) => {
    if (!current) return current;               // match vanished — abort
    if (current.status !== "waiting") return;   // abort: already started
    if (current.players && current.players[1]) return; // abort: seat taken
    return {
      ...current,
      status: "active",
      activePlayer: 0,
      players: { ...current.players, 1: player2 },
    };
  });

  if (!result.committed) return { error: "Match is full." };
  return { success: true };
};

// ── Get match once ────────────────────────────────────────────
export const getMatch = async (code) => {
  const matchRef = ref(db, `matches/${code}`);
  const snapshot = await get(matchRef);
  return snapshot.exists() ? snapshot.val() : null;
};

// ── Listen to match changes in real time ──────────────────────
export const listenToMatch = (code, callback) => {
  const matchRef = ref(db, `matches/${code}`);
  // onValue returns an unsubscribe function — use it directly
  // This allows multiple independent listeners on the same path
  const unsub = onValue(matchRef, (snapshot) => {
    if (snapshot.exists()) callback(snapshot.val());
  });
  // Return the specific unsubscribe, not off() which kills ALL listeners
  return unsub;
};

// ── Update game state ─────────────────────────────────────────
export const updateMatch = async (code, updates) => {
  const matchRef = ref(db, `matches/${code}`);
  await update(matchRef, updates);
};

// ── Update a specific player's state ─────────────────────────
export const updatePlayer = async (code, playerIdx, updates) => {
  const playerRef = ref(db, `matches/${code}/players/${playerIdx}`);
  await update(playerRef, updates);
};

// ── End the match ─────────────────────────────────────────────
export const endMatch = async (code, winnerIdx) => {
  const matchRef = ref(db, `matches/${code}`);
  await update(matchRef, {
    status: "finished",
    winner: winnerIdx,
  });
};

// ── Draw cards from grimoire ───────────────────────────────────
// drawAndDeplete — draws cards AND returns depleted grimoire
// This is the correct way to draw — always use this
export const drawAndDepleteCards = (grimoire, currentHand, count) => {
  const pool = [];
  const newGrimoire = { ...grimoire };
  Object.entries(grimoire).forEach(([cardId, qty]) => {
    for (let i = 0; i < qty; i++) pool.push(cardId);
  });
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  const drawn = pool.slice(0, count);
  drawn.forEach(id => {
    if (newGrimoire[id] > 1) newGrimoire[id]--;
    else delete newGrimoire[id];
  });
  return { hand: [...currentHand, ...drawn], grimoire: newGrimoire };
};

export const drawCards = (grimoire, currentHand, count) => {
  const pool = [];
  Object.entries(grimoire).forEach(([cardId, qty]) => {
    for (let i = 0; i < qty; i++) pool.push(cardId);
  });

  // Shuffle pool
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }

  const drawn = pool.slice(0, count);
  return [...currentHand, ...drawn];
};

// ── Shuffle an array ──────────────────────────────────────────
export const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};